import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import {
  plato_api_single_attempt_fitb_grading_attrs_question_path,
  plato_api_multi_attempt_fitb_grading_attrs_question_path,
} from '@/modules/routes';
import { StudentAnswerProps } from '@/components/staffer/Grading/types';
import { canViewCorrectAnswers, noAnswerProvided, renderCorrectnessIcon } from '@/components/staffer/Grading/utils';

interface SingleAttemptFitbRowProps {
  answerOptionsIndex: number;
  questionAnswerId: number;
  questionId: number;
  showCorrectAnswers: boolean;
}

const SingleAttemptFitbRow = ({
  answerOptionsIndex,
  questionAnswerId,
  questionId,
  showCorrectAnswers,
}: SingleAttemptFitbRowProps) => {
  // AIDEV-NOTE: PLAT-17195 — `questionAnswerId` must be in the key. The endpoint returns
  // student-specific `is_choice_correct` and `student_selected_choice_text` values, and the
  // provider disables `refetchOnMount`, so without it a previous student's response is served
  // from cache when the teacher navigates to a new student.
  const fitbGradingAttrsQuery = useQuery(
    ['fitbGrading', questionId, answerOptionsIndex, questionAnswerId],
    () => (
      Axios.get(plato_api_single_attempt_fitb_grading_attrs_question_path(questionId, {
        answer_options_index: answerOptionsIndex,
        question_answer_id: questionAnswerId,
      }))
        .then(res => res.data)
    ),
  );

  if (fitbGradingAttrsQuery.isLoading) {
    return (
      <tr>
        <td>Loading...</td>
        <td>Loading...</td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        {renderCorrectnessIcon(fitbGradingAttrsQuery.data.is_choice_correct)}
        {fitbGradingAttrsQuery.data.student_selected_choice_text}
      </td>
      {showCorrectAnswers && (
        <td>
          {fitbGradingAttrsQuery.data.correct_choice_text}
        </td>
      )}
    </tr>
  );
};

interface MultiAttemptFitbAttemptCellProps {
  answerOptionsIndex: number;
  attemptNumber: number;
  questionAnswerId: number;
  questionId: number;
  studentChoiceIndexForAttempt: string;
}

const MultiAttemptFitbAttemptCell = ({
  answerOptionsIndex,
  attemptNumber,
  questionAnswerId,
  questionId,
  studentChoiceIndexForAttempt,
}: MultiAttemptFitbAttemptCellProps) => {
  const fitbGradingAttrsQuery = useQuery(
    ['fitbGrading', questionId, answerOptionsIndex, studentChoiceIndexForAttempt],
    () => (
      Axios.get(plato_api_multi_attempt_fitb_grading_attrs_question_path(questionId, {
        answer_options_index: answerOptionsIndex,
        question_answer_id: questionAnswerId,
        student_choice_index_for_attempt: studentChoiceIndexForAttempt,
      }))
        .then(res => res.data)
    ),
  );

  if (fitbGradingAttrsQuery.isLoading) {
    return <td>LOADING</td>;
  }

  return (
    <>
      <td>
        {renderCorrectnessIcon(fitbGradingAttrsQuery.data.is_attempt_correct)}
        {fitbGradingAttrsQuery.data.text_for_attempt}
      </td>
      {attemptNumber === 1 && <td>{fitbGradingAttrsQuery.data.correct_choice_text}</td>}
    </>
  );
};

interface MultiAttemptFitbRowProps {
  answerOptionsIndex: number;
  gatherAttempts: string[];
  questionAnswerId: number;
  questionId: number;
}

const MultiAttemptFitbRow = ({
  answerOptionsIndex,
  gatherAttempts,
  questionAnswerId,
  questionId,
}: MultiAttemptFitbRowProps) => (
  <tr>
    {[0, 1].map(attemptNumber => (
      <MultiAttemptFitbAttemptCell
        answerOptionsIndex={answerOptionsIndex}
        attemptNumber={attemptNumber}
        key={attemptNumber}
        questionAnswerId={questionAnswerId}
        questionId={questionId}
        studentChoiceIndexForAttempt={gatherAttempts[attemptNumber][answerOptionsIndex]}
      />
    ))}
  </tr>
);

const FillInTheBlankQuestionStudentAnswer = ({ question, questionAnswer }: StudentAnswerProps) => {
  const showCorrectAnswers = canViewCorrectAnswers();

  if (!questionAnswer || questionAnswer.gather_attempts?.length === 0) return noAnswerProvided();

  if (questionAnswer.gather_attempts?.length !== 1) {
    return (
      <table className="fill-in-the-blank-answer-preview answer-preview-table tw-min-w-[475px]">
        <thead>
          <tr>
            <td>Selected answer (1st)</td>
            <td>Selected answer (2nd)</td>
            <td>Correct answer</td>
          </tr>
        </thead>
        <tbody>
          {question.choices_per_blank.map((_, answerOptionsIndex) => (
            <MultiAttemptFitbRow
              answerOptionsIndex={answerOptionsIndex}
              gatherAttempts={questionAnswer.gather_attempts}
              key={answerOptionsIndex}
              questionAnswerId={questionAnswer.id}
              questionId={question.id}
            />
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className="fill-in-the-blank-answer-preview answer-preview-table tw-min-w-[475px]">
      <thead>
        <tr>
          <td>Selected answer</td>
          {showCorrectAnswers && <td>Correct answer</td>}
        </tr>
      </thead>
      <tbody>
        {question.choices_per_blank.map((_, answerOptionsIndex) => (
          <SingleAttemptFitbRow
            answerOptionsIndex={answerOptionsIndex}
            key={answerOptionsIndex}
            questionAnswerId={questionAnswer.id}
            questionId={question.id}
            showCorrectAnswers={showCorrectAnswers}
          />
        ))}
      </tbody>
    </table>
  );
};

export default FillInTheBlankQuestionStudentAnswer;
